/**
 * useGameEntry —— **外壳直解游戏版本入口**（省掉「转接页」那一趟 HTTP 往返）。
 *
 * ## 现状（四层进场）
 *
 * ```
 * ① 外壳 Vue SPA → ② iframe → ③ 转接页 games/<gameId>/index.html → ④ 游戏 <sha>/index.html
 * ```
 *
 * 第 ③ 层的转接页做的事只有：`fetch('./latest.json')` 拿到 `path`（= build 的 sha 目录），
 * 再 `location.replace('./<path>/index.html' + search + hash)` 整页跳走。也就是说，玩家要
 * **多下载并执行一个 HTML、多等一个 fetch、再整页导航一次**，才开始载真正的游戏。
 *
 * 本模块把那一趟搬到外壳来做：外壳解析出 `gameUrl` 之后，自己去 fetch 同目录的
 * `latest.json`，成功就把 iframe **直接指向** `<同目录>/<path>/index.html`，转接页那一层
 * 整个跳过。
 *
 * ## 🛑 转接页不删，降级为保底（盲点 4）
 *
 * 转接页 `games/<gameId>/index.html` **必须继续存在**。任何一种失败（fetch 挂掉 / 3 秒逾时 /
 * JSON 坏掉 / 缺 `path` / 算出来的 URL 非同源）都走回原本的 `gameUrl`，也就是让转接页照旧工作。
 * 这条保底同时让「没有 sha 目录的环境」（dev / stg 平铺部署，`latest.json` 直接 404）行为
 * **与现在完全相同**（盲点 7）。
 *
 * ## 🛑 prod 快取规则要一起改（盲点 6）
 *
 * 部署侧若对 `games/<id>/` 底下设「除 `latest.json` 外通通长快取」，**必须再排掉
 * `games/<id>/index.html`**：
 *
 *   - `games/<id>/latest.json`   → no-store / no-cache（本来就要排除）
 *   - `games/<id>/index.html`    → **也要排除**（它是保底转接页，被长快取住的话，
 *                                   直解失败时玩家会跳到旧 sha，等于版本卡死）
 *   - `games/<id>/<sha>/**`      → 可以长快取（路径含 sha，天然 immutable）
 *
 * ## 🛑 只在 prod 生效（其余环境连 fetch 都不发）
 *
 * `latest.json` 与 `<sha>/` 子目录**只有 prod 才有**（CI 的 `append_commit_sha` 在 prod 一律
 * 自动启用、非 prod 默认关）。dev / stg / rel 都是平铺部署，直解必定失败、必定走保底 ——
 * 与其每次开游戏白发一个 404 往返，不如在入口直接判掉。
 *
 * 这条判断同时把「只在 prod 生效」从**外部条件**（那些环境刚好没有 `latest.json`）
 * 升级成**程式码保证**：哪天有人在 rel 开了 `append_commit_sha`，也不会突然开始直解。
 *
 * 🛑 `APP_ENV` 是 `runtimeConfig.ts` 的 **live binding（`export let`）**，module 初始化当下
 *    还是空字串、`initRuntimeConfig()` 之后才有值 —— 所以判断**必须写在函式内**，
 *    不可以在 module scope 快照成 const（见 `runtimeConfig.ts` 档头警告）。
 *
 * ## 相关
 *
 *   - 协议背景：`docs/LOADING_PROTOCOL.md`
 *   - 版本角标读的 `gameConfig.json` 仍走**原本的** `gameUrl` 同目录（`useVersionInfo.ts`），
 *     本模块不改那条线。
 */

import { APP_ENV, AppEnv } from '@/runtimeConfig'

/** `latest.json` 的形状（只有 `path` 是我们真正要的；其余栏位仅供人看） */
interface LatestJson {
  sha?: string
  path?: string
  ref?: string
  updatedAt?: string
}

/** 直解结果：`src` 一定可以直接塞进 iframe（失败时 = 原本的 gameUrl） */
export interface GameEntryResolution {
  /** 要给 iframe 的最终 URL */
  src: string
  /** true = 直解成功（跳过转接页）；false = 保底走转接页 */
  direct: boolean
  /** 直解成功时的 sha 目录名（= latest.json 的 path） */
  path?: string
}

/** 自订逾时：3 秒内没拿到 latest.json 就放弃直解、走保底（盲点 4） */
export const LATEST_TIMEOUT_MS = 3000

/**
 * 解析 gameUrl 成 URL 物件。
 * 各款 content.ts 的 gameUrl 是绝对网址（`${GAME_H5_URL}/games/<code>/index.html`），
 * 但这里仍给 base 兜底，让相对路径与 node（vitest，无 window）环境都能解析。
 */
function parseGameUrl(gameUrl: string): URL | null {
  if (!gameUrl || typeof gameUrl !== 'string') return null
  try {
    const base = typeof window !== 'undefined' ? window.location.href : 'http://localhost/'
    return new URL(gameUrl, base)
  } catch {
    return null
  }
}

/** URL 的「同目录」pathname（含结尾斜线）：`/games/sybs/index.html` → `/games/sybs/` */
function dirOf(url: URL): string {
  const i = url.pathname.lastIndexOf('/')
  return i >= 0 ? url.pathname.slice(0, i + 1) : '/'
}

/**
 * gameUrl（`…/games/<code>/index.html`）→ 同目录的 `latest.json`。
 *
 * 🛑 一定要带 `?t=<now>`（盲点 5）：`cache: 'no-store'` 只管得住浏览器自己的 HTTP cache，
 *    管不住中间的 CDN / 代理；两者要一起上才保证拿到最新的 `path`。
 * 🛑 原 gameUrl 的 search / hash **不带进来** —— 那是要给游戏页的参数（gameId / token /
 *    语系），跟拿版本号无关，带上去只会污染快取键。
 */
export function latestJsonUrl(gameUrl: string, now: number = Date.now()): string | null {
  const u = parseGameUrl(gameUrl)
  if (!u) return null
  return `${u.origin}${dirOf(u)}latest.json?t=${now}`
}

/**
 * 用 latest.json 的 `path` 拼出直解入口：`<gameUrl 同目录>/<path>/index.html` + 原 search + hash。
 *
 * - 盲点 2：**query / hash 原样转发** —— 转接页本来做的就是
 *   `location.replace('./<path>/index.html' + location.search + location.hash)`；
 *   gameId / token / 语系全在里面，掉了游戏就起不来。
 * - 盲点 4：算出来的 URL **必须同源、且仍在原目录底下**，否则回 `null` 让呼叫端走保底。
 *   （`path` 是远端 JSON 给的字串，要当成不可信输入：`../`、`//evil.com`、`https://…`
 *   都得挡掉。）
 */
export function buildDirectEntry(gameUrl: string, path: string): string | null {
  const u = parseGameUrl(gameUrl)
  if (!u) return null
  if (!path || typeof path !== 'string') return null
  // 明显不是「目录名」的一律挡：协议、绝对路径、上层跳脱、反斜线、空白
  if (/[:\\\s]/.test(path)) return null
  if (path.startsWith('/') || path.startsWith('.')) return null
  if (path.split('/').some((seg) => seg === '' || seg === '.' || seg === '..')) return null

  const dir = dirOf(u)
  let direct: URL
  try {
    direct = new URL(`${dir}${path}/index.html`, u.origin)
  } catch {
    return null
  }
  // 同源 + 仍在原目录底下，两条都过才算数（盲点 4）
  if (direct.origin !== u.origin) return null
  if (!direct.pathname.startsWith(dir)) return null

  return `${direct.origin}${direct.pathname}${u.search}${u.hash}`
}

/**
 * 直解游戏入口：fetch 同目录 latest.json，成功 → 直指 `<sha>/index.html`；任何失败 → 原 gameUrl。
 *
 * 🛑 **不做任何记忆体快取**（盲点 3）：外壳是 SPA、切游戏不会重载页面，快取住就会拿到上一款 /
 *    上一版的 sha。每次开游戏都重新 fetch，成本只有一个小 JSON。
 *
 * @param gameUrl   原本要给 iframe 的 URL（= 转接页）
 * @param fetchFn   注入点（测试用）
 * @param timeoutMs 逾时毫秒（预设 3000）
 */
export async function resolveGameEntry(
  gameUrl: string,
  fetchFn: typeof fetch = typeof fetch !== 'undefined' ? fetch : (undefined as unknown as typeof fetch),
  timeoutMs: number = LATEST_TIMEOUT_MS,
): Promise<GameEntryResolution> {
  // 保底 = 原路走转接页（盲点 4 / 盲点 7）
  const fallback: GameEntryResolution = { src: gameUrl, direct: false }

  // 只有 prod 有 `latest.json` / `<sha>/`；其余环境直接保底，连 fetch 都不发（见档头「只在 prod 生效」）。
  // APP_ENV 是 live binding，必须在函式内读，不可在 module scope 快照。
  if (APP_ENV !== AppEnv.Prod) return fallback

  const url = latestJsonUrl(gameUrl)
  if (!url || typeof fetchFn !== 'function') return fallback

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null

  try {
    // 盲点 5：`no-store` + `?t=` 两者都要（前者挡浏览器快取、后者挡 CDN / 代理）
    const res = await fetchFn(url, { cache: 'no-store', signal: controller?.signal })
    // 盲点 1：**不能只看状态码** —— 没有 latest.json 的环境（SPA host / 单页改写）很可能把
    //   404 改写成 200 + index.html。所以 `r.ok` 之外还要真的解析出 `typeof path === 'string'`
    //   且非空，否则一律当失败。（回 HTML 时 `res.json()` 会直接 throw，落到下面 catch。）
    if (!res.ok) return fallback
    const json = (await res.json()) as LatestJson | null
    const path = json && typeof json.path === 'string' ? json.path.trim() : ''
    if (!path) return fallback

    const src = buildDirectEntry(gameUrl, path)
    if (!src) return fallback
    return { src, direct: true, path }
  } catch {
    // fetch 失败 / abort 逾时 / JSON 解析失败 → 保底（盲点 4）
    return fallback
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * 直解结果印一行（沿用本专案 `[web][<Module>]` 的 log 惯例，见 `useVersionInfo.ts`）。
 *
 * 用 `console.info`（非 `debug`）：debug 级别在浏览器预设 log level 下看不到。
 * ⚠️ sandbox / rel / prod 是 release build，esbuild `drop: ['console']` 会把所有 console
 *    整包移除 —— 这是本专案既有政策（同 `[web][Version]`），不是这里的选择；要在正式站看这行，
 *    得走 dev / stg（debug build）。
 */
export function logGameEntry(result: GameEntryResolution): void {
  console.info(
    `[web][GameEntry] game entry resolved: ${result.direct ? `direct ${result.path}` : 'fallback redirect page'}`,
  )
}
